import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ITEComunicacionOutput } from "components/ITE/types/comunicaciones";
import { ActivoCompletoOutput, PortfolioStats } from "components/Portfolio/types/activos";
import { PortfolioFilterSelection } from "components/Portfolio/types/filterData";
import { ApiPaginatedResponse, StatePropWithThunkState, THUNK_STATE } from "config/types";

import { SLICE_NAMES } from "../constant";
import {
	getInfoActivosExtraReducer,
	getPortfolioStatssExtraReducer,
	putEditarActivoExtraReducer,
	postNuevoActivoValidadoExtraReducer,
	putRechazarActivoExtraReducer,
	putValidarActivoExtraReducer,
	putFechaEntregaCertificacionExtraReducer,
	putCertificacionBulkExtraReducer,
	postComentarioExtraReducer,
} from "./extraReducers";

export interface PortfolioSlice {
	allAssetsInfo: ActivoCompletoOutput[];
	assetsInfo: StatePropWithThunkState<ApiPaginatedResponse<ActivoCompletoOutput[]>>;
	portfolioStats: StatePropWithThunkState<PortfolioStats | null>;
	certificationActionThunkState: THUNK_STATE;
	commentActionThunkState: THUNK_STATE;
	filterSelection: PortfolioFilterSelection | null;
	orderBy: { column: keyof ActivoCompletoOutput; direction: "asc" | "desc" } | null;
	page: number;
	mapViewStats: "estadoITE" | "indiceNEC";
}

const sliceInitialState: PortfolioSlice = {
	allAssetsInfo: [],
	assetsInfo: {
		thunkState: THUNK_STATE.IDLE,
		data: {
			data: [],
			pagination: {
				total: -1,
				lastPage: -1,
				perPage: -1,
				currentPage: -1,
				from: -1,
				to: -1,
			},
		},
	},
	portfolioStats: {
		thunkState: THUNK_STATE.IDLE,
		data: null,
	},
	certificationActionThunkState: THUNK_STATE.IDLE,
	commentActionThunkState: THUNK_STATE.IDLE,
	filterSelection: null,
	orderBy: null,
	page: 1,
	mapViewStats: "indiceNEC",
};

const customSlice = createSlice({
	name: `${SLICE_NAMES.PORTFOLIO}`,
	initialState: sliceInitialState,
	reducers: {
		setFilterSelection: (state, action: PayloadAction<PortfolioFilterSelection>) => ({
			...state,
			filterSelection: { ...action.payload },
		}),
		resetFilterSelection: (state) => ({ ...state, filterSelection: null }),
		setOrderBy: (state, action: PayloadAction<{ column: keyof ActivoCompletoOutput; direction: "asc" | "desc" }>) => ({
			...state,
			orderBy: { ...action.payload },
		}),
		resetOrderBy: (state) => ({ ...state, orderBy: null }),
		setPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},
		editarComunicacionActivo: (
			state,
			action: PayloadAction<{ assetId: string; comunicacion: ITEComunicacionOutput }>,
		) => ({
			...state,
			allAssetsInfo: state.allAssetsInfo.map((a) =>
				a.id === action.payload.assetId
					? {
							...a,
							revised_at: action.payload.comunicacion.revised_at,
							type: action.payload.comunicacion.type,
							recipients: action.payload.comunicacion.recipients,
							response_date: action.payload.comunicacion.response_date,
							comunicacion_id: action.payload.comunicacion.id,
					  }
					: a,
			),
			assetsInfo: {
				...state.assetsInfo,
				data: {
					...state.assetsInfo.data,
					data: state.assetsInfo.data.data.map((a) =>
						a.id === action.payload.assetId
							? {
									...a,
									revised_at: action.payload.comunicacion.revised_at,
									type: action.payload.comunicacion.type,
									recipients: action.payload.comunicacion.recipients,
									response_date: action.payload.comunicacion.response_date,
									comunicacion_id: action.payload.comunicacion.id,
							  }
							: a,
					),
				},
			},
		}),
		clearAllAssetsInfo: (state) => ({ ...state, allAssetsInfo: [] }),
		setAllAssetsInfo: (state, action: PayloadAction<ActivoCompletoOutput[]>) => ({
			...state,
			allAssetsInfo: action.payload,
		}),
		setMapViewStats: (state, action: PayloadAction<"estadoITE" | "indiceNEC">) => {
			state.mapViewStats = action.payload;
		},
	},
	extraReducers: (builder) => {
		getInfoActivosExtraReducer(builder);
		getPortfolioStatssExtraReducer(builder);
		putEditarActivoExtraReducer(builder);
		putValidarActivoExtraReducer(builder);
		putRechazarActivoExtraReducer(builder);
		postNuevoActivoValidadoExtraReducer(builder);
		putFechaEntregaCertificacionExtraReducer(builder);
		putCertificacionBulkExtraReducer(builder);
		postComentarioExtraReducer(builder);
	},
});

export const actions = customSlice.actions;
export default customSlice.reducer;
